import styled from '@emotion/styled';
import { analyticsService, EventNames, ISchoolClassInfo } from '@taleemabad/db';
import {
  Button,
  Card,
  List,
  Select,
  SelectOption,
  Spacing,
  StyledDiv,
  TextField,
  TitleText,
  Typography,
} from '@taleemabad/dsm/components';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { getOrdinal, usePrint } from '@taleemabad/shared';
import React, { FC, useState } from 'react';

const behaviourOptions: SelectOption[] = [
  { content: 'Needs Improvement', value: 'NEEDS_IMPROVEMENT' },
  { content: 'Excellent', value: 'EXCELLENT' },
  { content: 'Fair', value: 'FAIR' },
];

export interface StudentCheckpointFormData {
  totalAcademicDays: number;
  daysPresent: number;
  classBehaviour: SelectOption;
}

export interface StudentSubjectMarks {
  subjectId: number | null;
  subjectLabel: string;
  obtainedMarks: number;
  totalMarks: number;
}

export interface StudentDetails {
  name: string;
  position: number;
  totalPercentage: number;
  totalOverallObtainedMarks: number;
  totalOverallMarks: number;
  subjectMarks: StudentSubjectMarks[];
  classBehaviour: string | null;
  totalAcademicDays: number | null;
  daysPresent: number | null;
}

interface ReportCardStudentResultsProps {
  classDetails: ISchoolClassInfo;
  studentDetails: StudentDetails;
  assessmentName: string;
  totalObtainedMarks: number;
  onFormSubmit: (formData: StudentCheckpointFormData) => void;
  onBackClick: () => void;
  onSubjectClick: (subjectId: number) => void;
}

export const ReportCardStudentResults: FC<ReportCardStudentResultsProps> = ({
  classDetails,
  studentDetails,
  assessmentName,
  totalObtainedMarks,
  onFormSubmit,
  onBackClick,
  onSubjectClick,
}) => {
  const [totalAcademicDays, setTotalAcademicDays] = useState<number | null>(
    studentDetails.totalAcademicDays,
  );
  const [daysPresent, setDaysPresent] = useState<number | null>(
    studentDetails.daysPresent,
  );
  const [classBehaviour, setClassBehaviour] = useState<SelectOption | null>(
    behaviourOptions.find(
      (option) => option.value === studentDetails.classBehaviour,
    ) || null,
  );
  const [daysPresentError, setDaysPresentError] = useState<string>('');

  const isSaveButtonDisabled =
    !totalAcademicDays || !daysPresent || !classBehaviour || !!daysPresentError;

  const { contentRef, handlePrint } = usePrint({});

  const handleTotalAcademicDaysInput = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = Number(e.target.value);
    setTotalAcademicDays(value);
    if (daysPresent !== null && value >= daysPresent) {
      setDaysPresentError('');
    } else if (daysPresent !== null && value < daysPresent) {
      setDaysPresentError(
        'Total Academic Days cannot be less than Days Present',
      );
    }
  };

  const handleDaysPresentInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setDaysPresent(value);
    if (totalAcademicDays !== null && value > totalAcademicDays) {
      setDaysPresentError(
        'Days present cannot be more than Total Academic Days',
      );
    } else {
      setDaysPresentError('');
    }
  };

  const handlePrintClick = () => {
    analyticsService.trackEvent(EventNames.reportCardPrintClicked, {
      epScreenName: 'Student Results',
    });
    handlePrint();
  };

  return (
    <>
      <PrintAndDownloadButtonContainer>
        <Button
          icon="download"
          variant="text"
          onClick={handlePrintClick}
          iconColor="--md-ref-primary-50"
        >
          Download & Print
        </Button>
      </PrintAndDownloadButtonContainer>
      <PrintOnlyContent ref={contentRef}>
        <Spacing marginTop="xl" marginBottom="lg">
          <Typography variant="textTitleLarge" fullWidth={true}>
            {assessmentName} Results
          </Typography>
        </Spacing>

        <Spacing marginBottom="xxl">
          <StyledDiv gap={8} alignItem="center">
            <TitleText
              leftText={studentDetails.name}
              rightText={`${classDetails.gradeLabel} - ${classDetails.section}`}
              seperatorIconName="circle"
            />
          </StyledDiv>
        </Spacing>

        <ResultContentContainer>
          <Card variant="filled" minHeight="50px">
            <Spacing marginBottom="xl">
              <StyledListHeader>
                <Typography variant="textTitleMediumLightOnSurface">
                  <PositionContainer>
                    Position: {getOrdinal(studentDetails.position)}
                  </PositionContainer>
                </Typography>
                <Typography variant="textTitleMediumLightOnSurface">
                  Percentage:
                  {Math.round(
                    (totalObtainedMarks / studentDetails.totalOverallMarks) *
                      100,
                  )}
                  %
                </Typography>
              </StyledListHeader>
            </Spacing>

            <StyledListHeader>
              <Typography variant="textTitleMediumLightOnSurface">
                <PositionContainer>Total Marks:</PositionContainer>
              </Typography>
              <Typography variant="textTitleMediumLightOnSurface">
                {`${totalObtainedMarks}/${studentDetails.totalOverallMarks}`}
              </Typography>
            </StyledListHeader>
          </Card>

          <Spacing marginTop="xxl">
            <Typography variant="textBodyMedium">
              Select a subject to see student’s exam
            </Typography>
          </Spacing>

          <Spacing padding="none md" margin="xxl none sm none">
            <StyledListHeader>
              <Typography variant="textTitleMediumSurfaceVariant">
                Subject
              </Typography>
              <Typography variant="textTitleMediumSurfaceVariant">
                Marks
              </Typography>
            </StyledListHeader>
          </Spacing>
          {studentDetails.subjectMarks.map((subjectMark, index) => (
            <List
              key={subjectMark.subjectId}
              heading={subjectMark.subjectLabel}
              trailingSupportingText={`${totalObtainedMarks}/${subjectMark.totalMarks}`}
              trailingIcon={{
                source: 'keyboard_arrow_right',
                name: 'arrow_forward',
                type: 'icon',
              }}
              onClick={() =>
                subjectMark.subjectId && onSubjectClick(subjectMark.subjectId)
              }
            />
          ))}
        </ResultContentContainer>

        <PrintOnlyTypographyContainer>
          <Spacing marginTop="xxl">
            <Typography variant="textTitleMediumSurfaceVariant">
              Attendance
            </Typography>
          </Spacing>
          <Spacing marginBottom="xxl">
            <Typography variant="textTitleMediumSurfaceVariant">
              Total Academic Days: {totalAcademicDays}
            </Typography>
            <Typography variant="textTitleMediumSurfaceVariant">
              Days Present: {daysPresent}
            </Typography>
          </Spacing>
          <Typography variant="textTitleMediumSurfaceVariant">
            Class Behaviour: {classBehaviour?.content}
          </Typography>
        </PrintOnlyTypographyContainer>
      </PrintOnlyContent>
      <InputContainer>
        <AttendanceContentContainer>
          <Typography variant="textTitleMediumSurfaceVariant">
            Attendance
          </Typography>
          <AttendanceInputContainer>
            <TextField
              variant="outlined"
              label="Total Academic Days"
              type="number"
              value={studentDetails.totalAcademicDays ?? ''}
              onInput={handleTotalAcademicDaysInput}
            />
            <TextField
              variant="outlined"
              label="Days Present"
              type="number"
              value={studentDetails.daysPresent ?? ''}
              onInput={handleDaysPresentInput}
              error={!!daysPresentError}
              errorText={daysPresentError}
            />
          </AttendanceInputContainer>
        </AttendanceContentContainer>
        <ClassBehaviourContentContainer>
          <Spacing marginTop="xl">
            <Typography variant="textTitleMediumSurfaceVariant">
              Class Behaviour
            </Typography>
          </Spacing>
          <Select
            label="Class Behaviour"
            defaultValue={studentDetails.classBehaviour}
            options={behaviourOptions}
            variant="outlined"
            width="100%"
            onChange={setClassBehaviour}
          />
        </ClassBehaviourContentContainer>
      </InputContainer>
      <ButtonContainer>
        <Button onClick={onBackClick} variant="outlined" children="Back" />
        <Button
          onClick={() => {
            onFormSubmit({
              totalAcademicDays: totalAcademicDays!,
              daysPresent: daysPresent!,
              classBehaviour: classBehaviour!,
            });
          }}
          disabled={isSaveButtonDisabled}
          variant="filled"
          children="Save Details"
        />
      </ButtonContainer>
    </>
  );
};

const PrintOnlyContent = styled.div`
  @media print {
    @page {
      margin: var(--rk-spacing-xxxxxxxxxxxl);
    }
  }
`;
const PrintOnlyTypographyContainer = styled.div`
  display: none;
  @media print {
    display: block;
  }
`;

const ResultContentContainer = styled.div`
  max-width: 400px;
`;

const StyledListHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const PositionContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-xs);
`;

const InputContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-lg);
  margin-top: var(--rk-spacing-xl);
  align-items: flex-end;
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
    align-items: unset;
  }
`;

const AttendanceContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-lg);
`;

const AttendanceInputContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-lg);
  ${mq(BreakpointSizes.xs)} {
    flex-direction: column;
  }
`;

const ClassBehaviourContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--rk-spacing-lg);
  width: 350px;
  ${mq(BreakpointSizes.xs)} {
    width: unset;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: var(--rk-spacing-lg);
  margin-top: var(--rk-spacing-xxxxxl);
`;

const PrintAndDownloadButtonContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;
